/**
 * Seeker-facing application stage vocabulary.
 *
 * Maps the canonical normalized lifecycle (Prompt 1: `current_lifecycle_state` +
 * `application_source`) to a small, human progression WITHOUT exposing internal events,
 * IDs, or ATS metadata. Employment and SkillBridge use different wording over the SAME
 * underlying architecture. External milestones are seeker-REPORTED (never system-observed).
 */

const EMPLOYMENT = {
  started: 'Started',
  contacted: 'Employer Response',
  submitted: 'Applied',
  interview: 'Interview',
  offer: 'Offer',
  hired: 'Hired',
  not_selected: 'Not Selected',
  withdrawn: 'Withdrawn',
};

// SkillBridge is provider-directed; only states backed by real activity are ever produced.
const SKILLBRIDGE = {
  started: 'Started',
  contacted: 'Contacted Provider',
  submitted: 'Contacted Provider',
  interview: 'In Discussion',
  offer: 'Accepted',
  hired: 'Started Program',
  not_selected: 'Not Accepted',
  withdrawn: 'Withdrawn',
};

const TERMINAL = new Set(['hired', 'not_selected', 'withdrawn']);

export function applicationSource(app) {
  if (app?.application_source) return app.application_source;
  if (app?.opportunity_type === 'skillbridge') return 'skillbridge';
  if (app?.application_method && app.application_method !== 'in_app') return 'external';
  return 'employer_native';
}

export function lifecycleState(app) {
  if (app?.current_lifecycle_state) return app.current_lifecycle_state;
  if (app?.hired === true) return 'hired';
  const s = (app?.status || '').toLowerCase();
  if (s === 'applied' || s === 'pending') return 'submitted';
  if (['started', 'contacted', 'submitted', 'interview', 'offer', 'hired', 'not_selected', 'withdrawn'].includes(s)) return s;
  return app?.applied_at ? 'submitted' : 'started';
}

/** Human stage label for the given application, using source-appropriate vocabulary. */
export function stageLabel(app) {
  const state = lifecycleState(app);
  const src = applicationSource(app);
  const table = src === 'skillbridge' ? SKILLBRIDGE : EMPLOYMENT;
  return table[state] || 'Applied';
}

/** True when the current stage is seeker-reported (external) rather than system-observed. */
export function isReported(app) {
  return applicationSource(app) === 'external';
}

export function isTerminal(app) {
  return TERMINAL.has(lifecycleState(app));
}

export function isActive(app) {
  return !isTerminal(app);
}

/** Short source descriptor for the card ("SkillBridge" / "External" / null for native). */
export function sourceLabel(app) {
  const src = applicationSource(app);
  if (src === 'skillbridge') return 'SkillBridge';
  if (src === 'external') return 'External';
  return null;
}

/** Best available opportunity title / employer from the immutable snapshot (with fallbacks). */
export function opportunityTitle(app) {
  return app?.job_snapshot?.title || app?.opportunity_title || app?.job_title || 'Opportunity';
}

export function employerOrProvider(app) {
  return app?.job_snapshot?.employer_or_provider || app?.provider_name
    || app?.employer_name || app?.company_name || null;
}

export function lastActivityAt(app) {
  return app?.last_activity_at || app?.submitted_at || app?.applied_at
    || app?.contacted_at || app?.started_at || null;
}
